import {Component, OnInit, ViewChild} from '@angular/core';
import {MatDrawer} from '@angular/material/sidenav';
import {OutlineUnitBasicModel} from 'app/core/modules/rest/consent/response/outline-unit-basic.model';
import {Observable} from 'rxjs';
import {getLanguageEnumByLanguageCode, LanguageEnum} from 'app/core/enums/language.enum';
import {TextSizeEnum} from '../../../../display-consent/enums/text-size.enum';
import {Store} from '@ngrx/store';
import {AppState} from 'app/core/reducers';
import {AuthRestService} from 'app/core/modules/rest/authentication/auth-rest.service';
import {InformationUnitRestService} from 'app/core/modules/rest/informationunit/information-unit-rest.service';
import {InformationUnitModel} from 'app/core/modules/rest/informationunit/response/information-unit.model';
import {UpdateInformationUnitModel} from 'app/core/modules/rest/informationunit/request/update-information-unit.model';
import {ChangeUnitRelevance, CreateNewUnitInOutline, DeleteUnit, GetCMSOutline, GetInformationUnit, MoveUnitLevelDown,
    MoveUnitLevelUp, ReorderUnits, ResetEditorInfo, ResetInformationUnitInfo, SaveInformationUnit, UpdateUnitInOutline} from '../../../store/cms-actions';
import {CmsReducer} from '../../../store';
import {InformationUnitEditorComponent} from '../information-unit-editor/information-unit-editor.component';
import {YesNoDialogComponent, YesNoDialogData} from 'app/shared/dialogs/yes-no-dialog/yes-no-dialog.component';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {CMS_INDICATIONS} from 'app/core/services/routes.service';
import {IndicationBasicModel} from 'app/core/modules/rest/indication/response/indication-basic.model';
import {GetIndication} from '../../../../indication/store/indication-actions';
import {IndicationReducer} from '../../../../indication/store';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NotificationService} from 'app/shared/components/notification/service/notification.service';
import {filter, map} from 'rxjs/operators';
import {IndicationUnitsRequestModel} from '@core/modules/rest/consent/request/indication-units-request.model';
import {ChangeUnitLevelRequestModel} from '@core/modules/rest/informationunit/request/change-unit-level-request.model';
import {ChangeUnitOrderModel} from '@core/modules/rest/informationunit/request/change-unit-order.model';
import {DeleteUnitRequestModel} from '@core/modules/rest/consent/request/delete-unit-request.model';
import {ChangeUnitRelevanceRequestModel} from '@core/modules/rest/informationunit/request/change-unit-relevance-request.model';
import {RelevanceEnum} from '@core/modules/rest/indication/enums/relevance.enum';

@UntilDestroy()
@Component({
    selector: 'app-cms-indication-edit',
    templateUrl: './cms-indication-edit.component.html',
    styleUrls: ['./cms-indication-edit.component.scss']
})
export class CmsIndicationEditComponent implements OnInit {

    @ViewChild(InformationUnitEditorComponent) informationUnitEditor;
    loadingUnit$: Observable<boolean> = this.store.select(CmsReducer.loadingSelector);
    indication$: Observable<IndicationBasicModel> = this.store.select(IndicationReducer.indicationSelector);
    informationUnit$: Observable<InformationUnitModel> = this.store.select(CmsReducer.informationUnitSelector);
    outline$: Observable<OutlineUnitBasicModel[]> = this.store.select(CmsReducer.outlineSelector);

    treeData: OutlineUnitBasicModel[];
    currentUnitOrderId: number | undefined;
    consentId: string | undefined;
    activeOutlineTab = 0;
    selectedLanguage: LanguageEnum;
    appLanguage: LanguageEnum;
    reviewModeTextSize = TextSizeEnum.SMALL;

    indicationName: string;
    indicationId: string;
    headerDescription: string;

    constructor(private dialog: MatDialog,
                private router: Router,
                private route: ActivatedRoute,
                private translateService: TranslateService,
                private store: Store<AppState>,
                private informationUnitService: InformationUnitRestService,
                private authService: AuthRestService,
                private notificationService: NotificationService) {
    }

    ngOnInit(): void {
        this.indication$.pipe(untilDestroyed(this)).subscribe((indication: IndicationBasicModel) => {
            if (indication) {
                this.headerDescription = this.translateService.instant('CMS.HEADER_DESCRIPTION', {indicationName: indication.title});
            }
        });

        this.selectedLanguage = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode'));
        this.appLanguage = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode'));
        this.route.params.subscribe(
            (params: Params) => {
                const indicationId = params.id;
                this.indicationId = indicationId;
                const request: IndicationUnitsRequestModel = {
                    indicationId,
                    language: this.selectedLanguage
                };
                this.store.dispatch(GetCMSOutline({request}));
                this.store.dispatch(GetIndication({id: indicationId, language: this.selectedLanguage}));
                this.outline$.pipe(
                    untilDestroyed(this),
                    filter((value: OutlineUnitBasicModel[]) => !!value && value.length > 0),
                    map((value: OutlineUnitBasicModel[]) => this.treeData = value)
                ).subscribe(_ => {
                    if (this.treeData && this.treeData[0]) {
                        if (!this.currentUnitOrderId) {
                            this.currentUnitOrderId = this.treeData[0].unitOrder;
                            this.navigateToSelectedUnit(this.treeData[0]);
                        }
                    }
                });
            });
    }

    async changeLanguage(selectedLanguage: LanguageEnum): Promise<void> {
        let canContinue = true;
        if (this.informationUnitEditor.detectedChangesInForm()) {
            canContinue = await this.editedContentDialog();
        }
        if (canContinue) {
            this.selectedLanguage = selectedLanguage;
            this.informationUnitEditor.populateForm();
        }
    }

    async showInformationUnit(unit: OutlineUnitBasicModel): Promise<void> {
        let canContinue = true;
        if (this.informationUnitEditor && this.informationUnitEditor.detectedChangesInForm()) {
            canContinue = await this.editedContentDialog();
        }
        if (canContinue) {
            this.navigateToSelectedUnit(unit);
        }
    }

    async discardChanges(): Promise<void> {
        const confirmed = await this.discardChangesDialog();
        if (confirmed) {
            this.informationUnitEditor.populateForm();
        }
    }

    async getBack(): Promise<void> {
        let canContinue = true;
        if (this.informationUnitEditor.detectedChangesInForm()) {
            canContinue = await this.editedContentDialog();
        }
        if (canContinue) {
            this.store.dispatch(ResetEditorInfo());
            this.goToCms();
        }
    }

    async leave(): Promise<void> {
        let canContinue = true;
        if (this.informationUnitEditor.detectedChangesInForm()) {
            canContinue = await this.editedContentDialog();
        }
        if (canContinue) {
            this.logout();
        }
    }

    async editedContentDialog(): Promise<boolean> {
        const data: YesNoDialogData = {
            icon: 'warning',
            iconColor: 'warning',
            title: this.translateService.instant('DIALOGS.EDITED_CONTENT.TITLE'),
            message: this.translateService.instant('DIALOGS.EDITED_CONTENT.MESSAGE'),
            yesButtonText: this.translateService.instant('DIALOGS.EDITED_CONTENT.YES'),
            noButtonText: this.translateService.instant('DIALOGS.EDITED_CONTENT.NO')
        };

        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.disableClose = false;
        dialogConfig.data = data;
        const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);
        dialogRef.componentInstance.noEvent.subscribe(() => {
            dialogRef.close();
        });

        let confirmed = false;
        dialogRef.componentInstance.yesEvent.subscribe(() => {
            confirmed = true;
            dialogRef.close();
        });

        return dialogRef.afterClosed().toPromise().then(() => {
            return Promise.resolve(confirmed);
        });
    }

    async discardChangesDialog(): Promise<boolean> {
        const data: YesNoDialogData = {
            icon: 'warning',
            iconColor: 'warning',
            title: this.translateService.instant('DIALOGS.DISCARD_CHANGES.TITLE'),
            message: this.translateService.instant('DIALOGS.DISCARD_CHANGES.MESSAGE'),
            yesButtonText: this.translateService.instant('DIALOGS.DISCARD_CHANGES.YES'),
            noButtonText: this.translateService.instant('DIALOGS.DISCARD_CHANGES.NO')
        };

        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.disableClose = false;
        dialogConfig.data = data;
        const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);
        dialogRef.componentInstance.noEvent.subscribe(() => {
            dialogRef.close();
        });

        let confirmed = false;
        dialogRef.componentInstance.yesEvent.subscribe(() => {
            confirmed = true;
            dialogRef.close();
        });

        return dialogRef.afterClosed().toPromise().then(() => {
            return Promise.resolve(confirmed);
        });
    }

    saveChanges(request: UpdateInformationUnitModel): void {
        this.store.dispatch(SaveInformationUnit({
            saveUnitRequest: request, executed: (unit: InformationUnitModel) => {
                if (request.id) {
                    this.store.dispatch(UpdateUnitInOutline({unit, language: this.appLanguage}));
                    this.notificationService.success(this.translateService.instant('NOTIFICATION.UNIT_CHANGE_SUCCESS'));
                } else {
                    this.store.dispatch(CreateNewUnitInOutline({
                        unit,
                        indicationId: request.indicationId,
                        language: this.appLanguage
                    }));
                    this.currentUnitOrderId = unit.externalId;
                }
            }
        }));
    }

    closeMenu(drawer: MatDrawer): void {
        drawer.toggle();
        this.activeOutlineTab = 0;
    }

    navigateToSelectedUnit(unit: OutlineUnitBasicModel): void {
        this.store.dispatch(GetInformationUnit({unitId: unit.informationUnitId}));
        this.currentUnitOrderId = unit.unitOrder;
    }

    goToCms(): void {
        this.router.navigate([CMS_INDICATIONS]);
    }

    logout(): void {
        this.authService.logout();
    }

    async onCreateUnit(): Promise<void> {
        let canContinue = true;
        if (this.informationUnitEditor.detectedChangesInForm()) {
            canContinue = await this.editedContentDialog();
        }
        if (canContinue) {
            this.store.dispatch(ResetInformationUnitInfo());
            this.currentUnitOrderId = -1;
            this.informationUnitEditor.resetForm();
        }
    }

    moveUnitLevelUp(unit: OutlineUnitBasicModel): void {
        const request: ChangeUnitLevelRequestModel = {
            unit,
            language: this.appLanguage
        };
        this.store.dispatch(MoveUnitLevelUp({request}));
    }

    moveUnitLevelDown(unit: OutlineUnitBasicModel): void {
        const request: ChangeUnitLevelRequestModel = {
            unit,
            language: this.appLanguage
        };
        this.store.dispatch(MoveUnitLevelDown({request}));
    }

    reorderUnits(request: ChangeUnitOrderModel): void {
        this.store.dispatch(ReorderUnits({request}));
    }

    async deleteUnitConfirmationDialog(unit: OutlineUnitBasicModel): Promise<boolean> {

        const data: YesNoDialogData = {
            icon: 'delete',
            iconColor: 'error',
            title: this.translateService.instant('DIALOGS.DELETE_INFORMATION_UNIT.TITLE', {unitName: unit.informationUnitTitle}),
            message: this.translateService.instant('GENERAL.ARE_YOU_SURE_DELETE_DESCRIPTION'),
            yesButtonText: this.translateService.instant('DIALOGS.DELETE_INFORMATION_UNIT.YES_DELETE'),
            noButtonText: this.translateService.instant('DIALOGS.DELETE_INFORMATION_UNIT.NO')
        };

        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.disableClose = false;
        dialogConfig.data = data;
        const dialogRef = this.dialog.open(YesNoDialogComponent, dialogConfig);
        dialogRef.componentInstance.noEvent.subscribe(() => {
            dialogRef.close();
        });

        const confirmed = false;
        dialogRef.componentInstance.yesEvent.subscribe(() => {
            this.deleteUnit(unit);
            dialogRef.close();
        });

        return dialogRef.afterClosed().toPromise().then(() => {
            return Promise.resolve(confirmed);
        });
    }

    deleteUnit(unit: OutlineUnitBasicModel): void {
        const request: DeleteUnitRequestModel = {
            informationUnitId: unit.informationUnitId,
            language: this.appLanguage
        };
        this.store.dispatch(DeleteUnit({request}));
    }

    changeUnitRelevance(data: { event: RelevanceEnum; unit: OutlineUnitBasicModel }): void {
        const request: ChangeUnitRelevanceRequestModel = {
            unitOrder: data.unit.unitOrder,
            indicationId: data.unit.indicationId,
            newRelevance: data.event
        };
        this.store.dispatch(ChangeUnitRelevance({request}));
    }
}


